import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, RefreshCw, CheckCircle2, Link2, Unlink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dialog to map AEGIS clients to Google Ads MCC sub-accounts.
 * - Lists all sub-accounts under the configured MCC
 * - Lets the admin pick which AEGIS client each one corresponds to
 * - Saves the mapping (clients.google_ads_customer_id) and force-enables sea_active
 */
export default function GoogleAdsLinkDialog({ open, onOpenChange, onLinked }) {
  const [accounts, setAccounts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  // map: { gads_customer_id: aegis_client_id }
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(null); // currently-saving customer_id

  const loadAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const { data } = await axios.get(`${API}/google-ads/accounts`, { withCredentials: true });
      setAccounts(data.accounts || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du chargement des sous-comptes');
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const { data } = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(data || []);
    } catch (e) {
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoadingClients(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadAccounts();
      loadClients();
    } else {
      setDraft({});
    }
  }, [open, loadAccounts, loadClients]);

  // When clients arrive, pre-fill draft with existing mappings
  useEffect(() => {
    if (clients.length === 0 || accounts.length === 0) return;
    const existing = {};
    clients.forEach(c => {
      if (c.google_ads_customer_id) existing[c.google_ads_customer_id] = c.id;
    });
    setDraft(existing);
  }, [clients, accounts]);

  const linkAccount = async (customer_id, client_id) => {
    if (!client_id) return;
    setSaving(customer_id);
    try {
      await axios.put(
        `${API}/google-ads/clients/${client_id}/link`,
        { google_ads_customer_id: customer_id },
        { withCredentials: true }
      );
      toast.success('Mapping enregistré');
      // Update local clients state
      setClients(cs => cs.map(c =>
        c.id === client_id ? { ...c, google_ads_customer_id: customer_id, sea_active: true } : c
      ));
      // Clear any previous mapping that pointed to this client
      setClients(cs => cs.map(c =>
        c.google_ads_customer_id === customer_id && c.id !== client_id
          ? { ...c, google_ads_customer_id: null }
          : c
      ));
      if (onLinked) onLinked();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la liaison');
    } finally {
      setSaving(null);
    }
  };

  const unlinkAccount = async (customer_id) => {
    const client = clients.find(c => c.google_ads_customer_id === customer_id);
    if (!client) return;
    setSaving(customer_id);
    try {
      await axios.put(
        `${API}/google-ads/clients/${client.id}/link`,
        { google_ads_customer_id: '' },
        { withCredentials: true }
      );
      toast.success('Liaison supprimée');
      setClients(cs => cs.map(c =>
        c.id === client.id ? { ...c, google_ads_customer_id: null } : c
      ));
      setDraft(d => { const next = { ...d }; delete next[customer_id]; return next; });
      if (onLinked) onLinked();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la dissociation');
    } finally {
      setSaving(null);
    }
  };

  // Memoize counts and the base list of clients available for linking (those without an existing linkage)
  const unlinkedClients = useMemo(
    () => clients.filter(c => !c.google_ads_customer_id),
    [clients],
  );
  const linkedCount = useMemo(
    () => clients.filter(c => c.google_ads_customer_id).length,
    [clients],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="gads-link-dialog">
        <DialogHeader>
          <DialogTitle>Lier les comptes Google Ads aux clients AEGIS</DialogTitle>
          <DialogDescription>
            Pour chaque sous-compte de votre MCC, sélectionnez le client AEGIS correspondant. La liaison active automatiquement le mode SEA pour ce client.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={loadAccounts} disabled={loadingAccounts}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loadingAccounts ? 'animate-spin' : ''}`} /> Recharger
          </Button>
          <span className="text-xs text-neutral-500">
            {accounts.length} sous-compte(s) · {linkedCount} déjà lié(s)
          </span>
        </div>

        {loadingAccounts || loadingClients ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
        ) : accounts.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded p-4 text-sm text-amber-900">
            Aucun sous-compte trouvé. Vérifiez que le MCC contient bien des comptes clients enabled, ou cliquez sur Recharger.
          </div>
        ) : (
          <div className="border border-neutral-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-neutral-600">Sous-compte Google Ads</th>
                  <th className="text-left px-3 py-2 font-medium text-neutral-600">Client AEGIS</th>
                  <th className="text-right px-3 py-2 font-medium text-neutral-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((acc) => {
                  const linkedClient = clients.find(c => c.google_ads_customer_id === acc.customer_id);
                  const draftClientId = draft[acc.customer_id] || '';
                  const isLinked = !!linkedClient;
                  return (
                    <tr key={acc.customer_id} className="border-b border-neutral-100">
                      <td className="px-3 py-2.5 align-top">
                        <div className="font-medium text-neutral-900">{acc.name || '(sans nom)'}</div>
                        <div className="text-xs text-neutral-400 font-mono">{acc.customer_id} · {acc.currency} · {acc.time_zone}</div>
                        {acc.is_test && <span className="inline-block mt-0.5 text-[10px] uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Test</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <Select
                          value={draftClientId}
                          onValueChange={(v) => setDraft(d => ({ ...d, [acc.customer_id]: v }))}
                          disabled={isLinked || saving === acc.customer_id}
                        >
                          <SelectTrigger className="w-full" data-testid={`gads-client-select-${acc.customer_id}`}>
                            <SelectValue placeholder="— Sélectionner un client —" />
                          </SelectTrigger>
                          <SelectContent>
                            {unlinkedClients
                              .concat(linkedClient && !unlinkedClients.find(u => u.id === linkedClient.id) ? [linkedClient] : [])
                              .map(c => (
                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {isLinked && (
                          <div className="text-xs text-green-700 flex items-center gap-1 mt-1">
                            <CheckCircle2 className="w-3 h-3" /> Lié à <strong>{linkedClient.name}</strong>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {isLinked ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => unlinkAccount(acc.customer_id)}
                            disabled={saving === acc.customer_id}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`gads-unlink-${acc.customer_id}`}
                          >
                            <Unlink className="w-3.5 h-3.5 mr-1" /> Dissocier
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            disabled={!draftClientId || saving === acc.customer_id}
                            onClick={() => linkAccount(acc.customer_id, draftClientId)}
                            className="bg-orange-600 hover:bg-orange-700"
                            data-testid={`gads-link-${acc.customer_id}`}
                          >
                            <Link2 className="w-3.5 h-3.5 mr-1" />
                            {saving === acc.customer_id ? '...' : 'Lier'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
